"use client";

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { parseHtmlString, renderParsed } from '@/lib/richText';
import LANGS from './lida.langs.json';
import LidaConfigJson from './lida.config.json';
import { useLocale } from '@/hooks/useLocale';

// ============================================================
// WorldLida — runtime translator (ported from to-use/worldlida).
//
// NO LONGER THE DEFAULT. Site copy is translated deterministically from the
// message catalogs in `messages/` via `<T>` (src/lib/i18n/T.tsx), because <L>
// can only translate *after* hydration: the server HTML — the copy crawlers
// and non-JS clients actually read — always went out in English.
//
// Kept, and still wired up, for the two jobs a catalog cannot do:
//
//   1. Copy that isn't ours to translate ahead of time — product titles,
//      supplier descriptions, anything the backend returns at runtime.
//      See the shipping methods in app/[locale]/checkout/CheckoutFlow.tsx.
//   2. Locales that have no catalog yet (`CATALOG_LOCALES` in
//      lib/i18n/locales.ts). `<T>` hands those the English message and lets
//      <L> translate it in the browser, exactly as the whole site used to
//      behave. Writing `messages/<locale>.json` retires the fallback for that
//      language with no code change.
//
// Reach for `<T>` first. Reach for <L> only when there is genuinely no source
// string at build time.
//
// Decoupled from the old socket: <L> reads the active language from
// our i18n (the /[locale] segment) via useLocale().
// ============================================================

interface LangEntry {
  name: string;
  id: string;
  countryCode: string;
}

const LANG_MAP = LANGS as Record<string, LangEntry | undefined>;

interface RawConfig {
  endpoint?: string;
  defaultResets?: boolean;
  debug?: boolean;
  maxCacheEntries?: number;
  timeoutMs?: number;
  serviceClient?: string;
  fallbackSourceLanguage?: string;
}

const RAW: RawConfig =
  (LidaConfigJson as { lidaOptions?: RawConfig })?.lidaOptions ?? {};

const CONFIG = Object.freeze({
  endpoint:
    RAW.endpoint || 'https://translate.googleapis.com/translate_a/single',
  defaultResets: Boolean(RAW.defaultResets),
  debug: Boolean(RAW.debug),
  maxCacheEntries:
    typeof RAW.maxCacheEntries === 'number' && RAW.maxCacheEntries > 0
      ? RAW.maxCacheEntries
      : 500,
  timeoutMs:
    typeof RAW.timeoutMs === 'number' && RAW.timeoutMs > 0
      ? RAW.timeoutMs
      : 8000,
  serviceClient: RAW.serviceClient || 'gtx',
  fallbackSourceLanguage: RAW.fallbackSourceLanguage || 'auto',
});

function debugWarn(msg: string, ...rest: unknown[]): void {
  if (CONFIG.debug && typeof console !== 'undefined') {
    console.warn(`[Lida] ${msg}`, ...rest);
  }
}

// ============================================================
// LRU cache + inflight coalescing
// ============================================================

class LRUCache<K, V> {
  private readonly max: number;
  private readonly map = new Map<K, V>();
  constructor(max: number) {
    this.max = Math.max(1, max);
  }
  get(key: K): V | undefined {
    const v = this.map.get(key);
    if (v === undefined) return undefined;
    this.map.delete(key);
    this.map.set(key, v);
    return v;
  }
  set(key: K, value: V): void {
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value as K | undefined;
      if (oldest === undefined) break;
      this.map.delete(oldest);
    }
  }
  delete(key: K): void {
    this.map.delete(key);
  }
  entries(): [K, V][] {
    return [...this.map.entries()];
  }
  bulk(entries: [K, V][]): void {
    for (const [k, v] of entries) this.set(k, v);
  }
}

const resultCache = new LRUCache<string, string>(CONFIG.maxCacheEntries);
const inflight = new Map<string, Promise<string>>();

// ---- Persistent cache (localStorage) -----------------------------------
// Translations are stable, so persisting them means each string is fetched
// from Google Translate once ever, not once per page load.
const PERSIST_KEY = 'rubyn.lida.v1';

function hydrateCache(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as [string, string][];
    if (Array.isArray(parsed)) resultCache.bulk(parsed);
  } catch {
    /* ignore corrupt cache */
  }
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;
function persistCache(): void {
  if (typeof window === 'undefined') return;
  if (persistTimer) return; // debounce a burst of translations into one write
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      window.localStorage.setItem(PERSIST_KEY, JSON.stringify(resultCache.entries()));
    } catch {
      /* quota / privacy mode — degrade to in-memory only */
    }
  }, 1000);
}

hydrateCache();

const KEY_SEP = '';

function cacheKey(query: string, sl: string, tl: string): string {
  return `${sl}${KEY_SEP}${tl}${KEY_SEP}${query}`;
}

// ============================================================
// Errors & abort helpers
// ============================================================

class LidaError extends Error {
  cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'LidaError';
    if (cause !== undefined) this.cause = cause;
  }
}

function buildAbortError(): Error {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('Aborted', 'AbortError');
  }
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

function isAbort(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === 'AbortError';
}

// ============================================================
// Network
// ============================================================

interface LidaOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

async function fetchTranslation(
  query: string,
  sl: string,
  tl: string,
  timeoutMs: number,
): Promise<string> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  const url =
    `${CONFIG.endpoint}?client=${encodeURIComponent(CONFIG.serviceClient)}` +
    `&sl=${encodeURIComponent(sl)}` +
    `&tl=${encodeURIComponent(tl)}` +
    `&dt=t&q=${encodeURIComponent(query)}`;

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new LidaError(`Translate HTTP ${response.status}`);
    const data: unknown = await response.json();
    if (!Array.isArray(data) || !Array.isArray((data as unknown[])[0])) {
      throw new LidaError('Unexpected translate payload');
    }
    const segments = (data as unknown[][])[0] as unknown[][];
    let out = '';
    for (const s of segments) {
      if (Array.isArray(s) && typeof s[0] === 'string') out += s[0];
    }
    return out;
  } catch (err) {
    if (isAbort(err)) throw err;
    throw new LidaError('Translate failed', err);
  } finally {
    clearTimeout(timeout);
  }
}

// ============================================================
// Public: Lida()
// ============================================================

async function Lida(
  query: string,
  sl?: string,
  tl?: string,
  opts: LidaOptions = {},
): Promise<string> {
  if (!query) return query;

  const fromId = sl ? LANG_MAP[sl]?.id : undefined;
  const toId = tl ? LANG_MAP[tl]?.id : undefined;

  if (!toId) {
    if (tl) debugWarn(`unknown target language: "${tl}"`);
    return query;
  }
  if (sl && !fromId) debugWarn(`unknown source language: "${sl}" (using auto)`);
  if (fromId && fromId === toId) return query;

  const slEff = fromId || CONFIG.fallbackSourceLanguage;
  const key = cacheKey(query, slEff, toId);

  const cached = resultCache.get(key);
  if (cached !== undefined) return cached;

  let shared = inflight.get(key);
  if (!shared) {
    shared = fetchTranslation(
      query,
      slEff,
      toId,
      opts.timeoutMs ?? CONFIG.timeoutMs,
    )
      .then((result) => {
        resultCache.set(key, result);
        persistCache();
        return result;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, shared);
  }

  const safe = shared.catch((err) => {
    if (isAbort(err)) return query;
    debugWarn('translation failed, returning original', err);
    return query;
  });

  const { signal } = opts;
  if (!signal) return safe;
  if (signal.aborted) throw buildAbortError();

  return new Promise<string>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(buildAbortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
    safe.then(
      (v) => {
        signal.removeEventListener('abort', onAbort);
        resolve(v);
      },
      (err) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      },
    );
  });
}

function peekLida(
  query: string,
  sl?: string,
  tl?: string,
): string | undefined {
  if (!query) return query;
  const fromId = sl ? LANG_MAP[sl]?.id : undefined;
  const toId = tl ? LANG_MAP[tl]?.id : undefined;
  if (!toId) return undefined;
  if (fromId && fromId === toId) return query;
  return resultCache.get(
    cacheKey(query, fromId || CONFIG.fallbackSourceLanguage, toId),
  );
}

// ============================================================
// React component
// ============================================================

interface LidaTranslateProps {
  text: string;
  languageFrom?: string;
  languageTo?: string;
  reset?: boolean;
  fallback?: ReactNode;
  onError?: (err: unknown) => void;
}

function LidaTranslate({
  text,
  languageFrom,
  languageTo,
  reset,
  fallback,
  onError,
}: LidaTranslateProps) {
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const resetRef = useRef(reset);
  resetRef.current = reset;

  // First render MUST match the server (which has no cache/localStorage):
  // always start from the source text, then apply cache/translation in the
  // effect after mount. Prevents hydration mismatches.
  const [display, setDisplay] = useState<string>(() =>
    (reset ?? CONFIG.defaultResets) ? '' : text,
  );

  useEffect(() => {
    const peek = peekLida(text, languageFrom, languageTo);
    if (peek !== undefined) {
      setDisplay(peek);
      return;
    }
    setDisplay((resetRef.current ?? CONFIG.defaultResets) ? '' : text);

    const controller = new AbortController();
    let alive = true;

    Lida(text, languageFrom, languageTo, { signal: controller.signal })
      .then((result) => {
        if (alive) setDisplay(result);
      })
      .catch((err) => {
        if (isAbort(err) || !alive) return;
        setDisplay(text);
        onErrorRef.current?.(err);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [text, languageFrom, languageTo]);

  const rendered = useMemo(
    () => (display ? renderParsed(parseHtmlString(display)) : null),
    [display],
  );

  if (!display) return <>{fallback ?? null}</>;
  return <>{rendered}</>;
}

// ============================================================
// React hook
// ============================================================

interface UseLidaState {
  value: string;
  loading: boolean;
  error: unknown | null;
}

function useLida(
  text: string,
  languageFrom?: string,
  languageTo?: string,
): UseLidaState {
  // Start from source text to match SSR; cache/translation applied post-mount.
  const [state, setState] = useState<UseLidaState>(() => ({
    value: text,
    loading: true,
    error: null,
  }));

  useEffect(() => {
    const peek = peekLida(text, languageFrom, languageTo);
    if (peek !== undefined) {
      setState({ value: peek, loading: false, error: null });
      return;
    }
    setState((s) => ({ value: s.value, loading: true, error: null }));

    const controller = new AbortController();
    let alive = true;

    Lida(text, languageFrom, languageTo, { signal: controller.signal })
      .then((result) => {
        if (alive) setState({ value: result, loading: false, error: null });
      })
      .catch((err) => {
        if (isAbort(err) || !alive) return;
        setState({ value: text, loading: false, error: err });
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [text, languageFrom, languageTo]);

  return state;
}

// ============================================================
// <L> — auto-localised wrapper using the active /[locale] language
// ============================================================

const L = memo(function L(props: LidaTranslateProps) {
  // Target language: explicit prop, otherwise the active locale's WorldLida
  // code derived from the URL (/[locale]).
  const locale = useLocale();
  const languageTo = props.languageTo ?? locale.lidaCode;

  // Skip a pointless same-language round-trip (e.g. en→en on the default
  // locale): render the source text (parsed) as-is.
  if (!languageTo || languageTo === (props.languageFrom ?? 'en')) {
    return <>{renderParsed(parseHtmlString(props.text))}</>;
  }

  return <LidaTranslate {...props} languageTo={languageTo} />;
});

// ============================================================
// Exports
// ============================================================

export default LidaTranslate;
export {
  Lida,
  peekLida,
  useLida,
  LidaError,
  L,
  CONFIG as LidaResolvedConfig,
};
export type { LidaTranslateProps, LidaOptions, UseLidaState };
